'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import AppIcon from '@/components/content/app/AppIcon'
import Popover from '@/components/ui/Popover'
import MobileMenu from '@/components/ui/MobileMenu'
import { Icon } from '@/icons/Icon'
import { cn, getBrandColors, isPopulatedObject } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { MenuItem } from '@/helpers/menus'
import type { App, Media } from '@/payload-types'

const variants = cva(
  'rounded-full shrink-0 h-10 pl-[3px] pr-3.5 py-1 gap-3 w-full max-w-[320px] inline-flex items-center justify-between transition-colors ring-offset-background duration-300 ease-in-out focus:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      style: {
        default:
          'border border-gray-200 bg-white text-gray-400 hover:border-primary-500/20 hover:text-primary-500',
        invert:
          'border border-white/20 bg-white/5 hover:border-primary-500/20 hover:bg-primary-500/10 hover:text-primary-500',
        active: 'border border-primary-500/20 bg-primary-500/10 text-primary-500',
      },
    },
    defaultVariants: {
      style: 'default',
    },
  },
)

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof variants> & {
    menu: MenuItem[]
    label: string
    apps?: App[]
    size?: 'default' | 'large'
    style?: 'default' | 'invert' | 'active'
    active: boolean
    align?: 'left' | 'right'
    onClick: () => void
  }

export default function Select({
  menu,
  label,
  apps,
  style,
  active,
  align,
  onClick,
  className,
  ...props
}: ButtonProps) {
  const { md } = useBreakpoint()

  const buttonContent = (
    <button className={cn(variants({ style, className }))} {...props}>
      <div className="flex items-center">
        {apps &&
          apps.map((app, index) => {
            const appIcon = app?.icon as Media
            const appGlyph = app?.glyph as Media
            if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
              return null
            }
            const { backgroundColor } = getBrandColors(app)
            return (
              <AppIcon
                key={app.id}
                icon={appIcon}
                glyph={appGlyph}
                name={app.name}
                background={backgroundColor}
                size={32}
                invert={true}
                circular={true}
                className={cn('shadow-sm', index > 0 ? (apps.length > 3 ? `-ml-4` : `-ml-2`) : '')}
                style={{ zIndex: apps.length - index }}
              />
            )
          })}
      </div>
      <span className="w-full truncate font-semibold text-white">{label}</span>
      <div
        className={cn('-rotate-90 text-white/30 transition-transform duration-300 ease-in-out', {
          'rotate-90': style === 'active',
        })}
      >
        <Icon name="chevron-left" size={12} />
      </div>
    </button>
  )

  if (!md) {
    return <MobileMenu items={menu}>{buttonContent}</MobileMenu>
  }

  return (
    <Popover
      menu={menu}
      visible={active}
      setVisible={onClick}
      size={40}
      align={align}
      shouldScroll={true}
    >
      {buttonContent}
    </Popover>
  )
}
